/**
 * OverviewTab - Performance score + 6 metric cards with trend arrows
 */
import { FC, useMemo, useState } from 'react';
import { Card, CardContent, Button } from '@pagespeedify/ui';
import { TrendingUp, TrendingDown, Minus, Activity } from 'lucide-react';
import { MonitoredUrl, MetricStats, FlattenedMonitoringHistory } from '../../../types';

interface OverviewTabProps {
    history: FlattenedMonitoringHistory[];
    fieldDataHistory: any[]; // Field data from additional_metrics.field_data_history
    stats: Record<string, MetricStats>;
    selectedUrl: MonitoredUrl | null;
}

const METRIC_CONFIG = {
    performance_score: { label: 'Performance Score', unit: '', divisor: 1, decimals: 0, inverse: true },
    lcp: { label: 'LCP', unit: 's', divisor: 1000, decimals: 2, inverse: false },
    cls: { label: 'CLS', unit: '', divisor: 1, decimals: 3, inverse: false },
    tbt: { label: 'TBT', unit: 'ms', divisor: 1, decimals: 0, inverse: false },
    fcp: { label: 'FCP', unit: 's', divisor: 1000, decimals: 2, inverse: false },
    tti: { label: 'TTI', unit: 's', divisor: 1000, decimals: 2, inverse: false },
    ttfb: { label: 'TTFB', unit: 'ms', divisor: 1, decimals: 0, inverse: false },
};

const THRESHOLDS = {
    performance_score: { good: 90, poor: 50 },
    lcp: { good: 2500, poor: 4000 },
    cls: { good: 0.1, poor: 0.25 },
    tbt: { good: 200, poor: 600 },
    fcp: { good: 1800, poor: 3000 },
    tti: { good: 3800, poor: 7300 },
    ttfb: { good: 800, poor: 1800 },
};

function getStatusColor(metric: string, value: number): string {
    const threshold = THRESHOLDS[metric as keyof typeof THRESHOLDS];
    if (!threshold) return 'text-muted-foreground';

    if (metric === 'performance_score') {
        if (value >= threshold.good) return 'text-green-600 dark:text-green-400';
        if (value <= threshold.poor) return 'text-red-600 dark:text-red-400';
        return 'text-amber-600 dark:text-amber-400';
    } else {
        if (value <= threshold.good) return 'text-green-600 dark:text-green-400';
        if (value >= threshold.poor) return 'text-red-600 dark:text-red-400';
        return 'text-amber-600 dark:text-amber-400';
    }
}

function getTrendIcon(trend: number | null, inverse: boolean) {
    if (trend === null) return { Icon: Minus, color: 'text-muted-foreground' };
    // For inverse metrics (like performance score), higher is better
    // For regular metrics (like LCP), lower is better
    const isImproving = inverse ? trend > 0 : trend < 0;
    const isDegrading = inverse ? trend < 0 : trend > 0;

    if (isImproving) return { Icon: TrendingUp, color: 'text-green-600 dark:text-green-400' };
    if (isDegrading) return { Icon: TrendingDown, color: 'text-red-600 dark:text-red-400' };
    return { Icon: Minus, color: 'text-muted-foreground' };
}

function formatValue(value: number | null, config: typeof METRIC_CONFIG[keyof typeof METRIC_CONFIG]): string {
    if (value === null) return '-';
    const converted = value / config.divisor;
    return converted.toFixed(config.decimals) + (config.unit ? ` ${config.unit}` : '');
}

interface StatCardProps {
    metric: string;
    config: typeof METRIC_CONFIG[keyof typeof METRIC_CONFIG];
    stat: MetricStats | undefined;
}

const StatCard: FC<StatCardProps> = ({ metric, config, stat }) => {
    if (!stat || stat.dataPoints === 0) {
        return (
            <Card className="relative overflow-hidden">
                <CardContent className="pt-6">
                    <div className="text-sm font-medium text-muted-foreground">{config.label}</div>
                    <div className="text-2xl font-bold text-muted-foreground mt-1">-</div>
                    <div className="text-xs text-muted-foreground mt-2">No data</div>
                </CardContent>
            </Card>
        );
    }

    const { Icon, color } = getTrendIcon(stat.trend, config.inverse);
    const trendText = stat.trend !== null ? `${stat.trend > 0 ? '+' : ''}${stat.trend.toFixed(1)}%` : '';
    const valueColor = getStatusColor(metric, stat.avg);

    return (
        <Card className="relative overflow-hidden hover:shadow-md transition-shadow">
            <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-muted-foreground">{config.label}</div>
                    {stat.trend !== null && (
                        <div className={`flex items-center gap-1 text-xs ${color}`}>
                            <Icon className="h-3 w-3" />
                            <span>{trendText}</span>
                        </div>
                    )}
                </div>
                <div className={`text-2xl font-bold mt-1 ${valueColor}`}>
                    {formatValue(stat.avg, config)}
                </div>
                <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                    <span>Min: {formatValue(stat.min, config)}</span>
                    <span>Max: {formatValue(stat.max, config)}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                    Based on {stat.dataPoints} scan{stat.dataPoints !== 1 ? 's' : ''}
                </div>
            </CardContent>
        </Card>
    );
};

export const OverviewTab: FC<OverviewTabProps> = ({ history, stats, selectedUrl }) => {
    const [period, setPeriod] = useState('7');

    // Filter history based on period
    const filteredHistory = useMemo(() => {
        if (period === 'all') return history;
        if (period === 'year') {
            const startOfYear = new Date(new Date().getFullYear(), 0, 1);
            return history.filter(h => new Date(h.scan_date) >= startOfYear);
        }
        const days = parseInt(period);
        const cutoff = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
        return history.filter(h => new Date(h.scan_date) >= cutoff);
    }, [history, period]);

    const isEmpty = filteredHistory.length === 0;

    // Calculate status summary
    const issueCount = useMemo(() => {
        if (!stats) return 0;
        let count = 0;
        if (stats.lcp?.avg > THRESHOLDS.lcp.poor) count++;
        if (stats.cls?.avg > THRESHOLDS.cls.poor) count++;
        if (stats.tbt?.avg > THRESHOLDS.tbt.poor) count++;
        return count;
    }, [stats]);

    return (
        <div className="space-y-6">
            {/* Period Selector - button toggle like History tab */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Activity className="h-5 w-5 text-muted-foreground" />
                    <span className="font-medium">Performance Overview</span>
                </div>
                <div className="flex gap-1 p-1 bg-muted/50 rounded-lg">
                    {[
                        { value: '7', label: '7 days' },
                        { value: '30', label: '30 days' },
                        { value: '90', label: '3 months' },
                        { value: 'year', label: 'This year' },
                        { value: 'all', label: 'All' },
                    ].map(opt => (
                        <Button
                            key={opt.value}
                            variant={period === opt.value ? 'default' : 'ghost'}
                            size="sm"
                            onClick={() => setPeriod(opt.value)}
                            className={`text-xs px-2 h-7 ${period === opt.value ? '' : 'text-muted-foreground hover:text-foreground'}`}
                        >
                            {opt.label}
                        </Button>
                    ))}
                </div>
            </div>

            {/* Status Summary */}
            {!isEmpty && (
                <Card className={issueCount === 0
                    ? 'bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-800'
                    : 'bg-amber-50 border-amber-200 dark:bg-amber-950/20 dark:border-amber-800'
                }>
                    <CardContent className="pt-4 pb-4">
                        <div className={`font-medium ${issueCount === 0 ? 'text-green-700 dark:text-green-300' : 'text-amber-700 dark:text-amber-300'}`}>
                            {issueCount === 0
                                ? '✓ Your site is performing well!'
                                : `⚠ ${issueCount} Core Web Vital${issueCount !== 1 ? 's' : ''} need${issueCount === 1 ? 's' : ''} attention`
                            }
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Empty State */}
            {isEmpty && (
                <Card>
                    <CardContent className="py-12 text-center text-muted-foreground">
                        <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>No scan data for the selected period.</p>
                        <p className="text-sm mt-1">Try selecting a longer time range or wait for more scans.</p>
                    </CardContent>
                </Card>
            )}

            {/* Metric Cards Grid */}
            {!isEmpty && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {Object.entries(METRIC_CONFIG).map(([metric, config]) => (
                        <StatCard
                            key={metric}
                            metric={metric}
                            config={config}
                            stat={stats[metric]}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default OverviewTab;
